import { createSlice } from "@reduxjs/toolkit";
import {
  addTagToProspect,
  createCampaign,
  createList,
  createTag,
  deleteList,
  launchCampaign,
  login,
  removeTagFromProspect,
  saveCampaign,
  updateCampaign,
  updateDailyQuota,
  updateList,
  updateOwner,
  updateUserProfile,
} from "../features/ownerActions";
import { List, Owner, Tag, Workspace } from "../../types";
import {
  clearStoredAuth,
  getStoredOwner,
  getStoredUser,
  setStoredOwner,
  setStoredUser,
  type StoredUser,
} from "../../utils/authStorage";

type SessionUser = {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  userBilling?: {
    stripeCustomerId?: string;
    PaymentTries?: number;
    stripePaymentFailure?: any;
    planhistory?: any[];
  } | null;
};

type LinkedOwnerSummary = {
  ownerId: string;
  email: string;
  name: string;
};

const toSessionUser = (user: StoredUser | null): SessionUser | null => {
  if (!user) return null;
  const userId = typeof (user as any)?.userId === "string" ? (user as any).userId : "";
  const email = typeof (user as any)?.email === "string" ? (user as any).email : "";
  if (!userId || !email) return null;
  const firstName = typeof (user as any)?.firstName === "string" ? (user as any).firstName : "";
  const lastName = typeof (user as any)?.lastName === "string" ? (user as any).lastName : "";
  const userBilling =
    (user as any)?.userBilling && typeof (user as any).userBilling === "object"
      ? (user as any).userBilling
      : null;
  return {
    userId,
    email,
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(userBilling ? { userBilling } : {}),
  };
};

const toLinkedOwners = (owner: Owner | null): LinkedOwnerSummary[] => {
  if (!owner?._id) return [];
  const fromPayload = Array.isArray((owner as any)?.linkedOwners)
    ? (owner as any).linkedOwners
        .map((entry: any) => ({
          ownerId: entry?.ownerId || entry?._id || "",
          email: entry?.email || "",
          name: entry?.name || "",
        }))
        .filter((entry: LinkedOwnerSummary) => entry.ownerId)
    : [];
  if (fromPayload.length > 0) return fromPayload;
  return [
    {
      ownerId: owner._id,
      email: owner.email || "",
      name: owner.name || "",
    },
  ];
};

const parseStoredWorkspace = () => {
  const raw = localStorage.getItem("currentWorkSpace");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const storedOwner = getStoredOwner<Owner>();
const storedUser = getStoredUser<StoredUser>();

export type OwnerState = {
  owner: Owner | null;
  currentUser: SessionUser | null;
  linkedOwners: LinkedOwnerSummary[];
  currentWorkspace: any;
  lists: List[];
  tags: Tag[];
  allProspectsList: List;
  listId: string;
  loading: boolean;
  error: string;
  success: boolean;
  newCampaignData: {
    ownerId: string;
    actions: any[];
    campaignStatus: string;
    campaignName: string;
    campaignId: string;
    listId: string;
    prospects: any[];
  };
  campaigns: any[];
  actionsAdded: string[];
  campaignId: string;
};

const initialState: OwnerState = {
  owner: storedOwner,
  currentUser: toSessionUser(storedUser),
  linkedOwners: toLinkedOwners(storedOwner),
  currentWorkspace: parseStoredWorkspace(),
  lists: [],
  tags: [],
  allProspectsList: {
    _id: "all",
    listName: "All Prospects",
    description: "All prospects in the workspace",
    prospects: [],
    ownerId: "",
    isDeleted: false,
    inActiveCampaign: false,
  },
  listId: "",
  loading: false,
  error: "",
  success: false,
  newCampaignData: {
    ownerId: (storedOwner as any)?._id || "",
    actions: [],
    campaignStatus: "ACTIVE",
    campaignName: "Invitation",
    campaignId: "",
    listId: "",
    prospects: [],
  },
  campaigns: [],
  actionsAdded: [],
  campaignId: "",
};

const ownerSlice = createSlice({
  name: "owner",
  initialState: initialState,
  reducers: {
    setOwner: (state, action) => {
      const nextOwner = action.payload;
      const normalizeOwnerUserId = (value: unknown): string => {
        if (value == null) return "";
        if (typeof value === "string") return value.trim();
        if (typeof value === "object" && "_id" in (value as object)) {
          return String((value as { _id?: unknown })._id || "").trim();
        }
        return String(value).trim();
      };
      const currentUserId = normalizeOwnerUserId(state.currentUser?.userId);
      const ownerUserId = normalizeOwnerUserId((nextOwner as any)?.userId);
      if (currentUserId && ownerUserId && currentUserId !== ownerUserId) {
        return;
      }
      state.owner = nextOwner;
      state.linkedOwners = toLinkedOwners(nextOwner);
      setStoredOwner(nextOwner);
    },
    setUser: (state, action) => {
      state.currentUser = action.payload;
      setStoredUser(action.payload);
    },
    setCurrentWorkSpace: (state, action) => {
      state.currentWorkspace = action.payload;
      localStorage.setItem("currentWorkSpace", JSON.stringify(action.payload));
    },
    setLists: (state, action) => {
      state.lists = action.payload.filter(
        (list: any) => list.isDeleted === false
      );
    },
    setAllProspectsList: (state, action) => {
      state.allProspectsList = action.payload;
    },
    logOut: (state) => {
      state.owner = null;
      state.currentUser = null;
      state.linkedOwners = [];
      clearStoredAuth();
      window.location.href = "/login";
    },
    setNewCampaignData: (state, action) => {
      state.newCampaignData = action.payload;
    },
    setCampaigns: (state, action) => {
      state.campaigns = action.payload;
    },
    setTags: (state, action) => {
      state.tags = action.payload;
    },
    setCampaignId: (state, action) => {
      state.campaignId = action.payload;
    },
    setListId: (state, action) => {
      state.listId = action.payload;
    },
    setActionsAdded: (state, action) => {
      state.actionsAdded = action.payload;
    },
    removeOwnerCredit: (state, action) => {
      if (state.owner?.credits) {
        for (let credit of state.owner.credits) {
          if (credit.ownerId && credit.ownerId === action.payload) {
            credit.ownerId = undefined;
          }
        }
      }
    }
  },
  extraReducers(builder) {
    // ❗❗ Login ❗❗
    builder.addCase(login.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(login.rejected, (state, action) => {
      // @ts-ignore
      state.error = action.payload;
      state.loading = false;
    });

    builder.addCase(login.fulfilled, (state, action) => {
      state.owner = action.payload.data;
      state.linkedOwners = toLinkedOwners(action.payload.data);
      state.loading = false;
      state.success = true;
      setStoredOwner(action.payload.data);
    });

    // ❗Add list❗
    builder.addCase(createList.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createList.rejected, (state, action) => {
      // @ts-ignore
      state.error = action.payload;
      state.loading = false;
    });

    builder.addCase(createList.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });

    // ❗Update List❗
    builder.addCase(updateList.pending, (state, action) => {
      state.loading = true;
    });

    builder.addCase(updateList.rejected, (state, action) => {
      // @ts-ignore
      state.error = action.payload;
      state.loading = false;
    });

    builder.addCase(updateList.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });

    // ❗Delete List❗

    builder
      .addCase(deleteList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(deleteList.rejected, (state, action) => {
        // @ts-ignore
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(deleteList.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lists = state.lists.filter(
          (list) => list._id !== action.payload._id
        );
      });

    // ❗Create campaign❗

    builder.addCase(createCampaign.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createCampaign.rejected, (state, action) => {
      // @ts-ignore
      state.error = action.payload;
      state.loading = false;
    });

    builder.addCase(createCampaign.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });
    builder.addCase(saveCampaign.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(saveCampaign.rejected, (state, action) => {
      // @ts-ignore
      state.error = action.payload;
      state.loading = false;
    });

    builder.addCase(saveCampaign.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });
    builder
      .addCase(updateOwner.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(updateOwner.rejected, (state, action) => {
        // @ts-ignore
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(updateOwner.fulfilled, (state, action) => {
        // console.log(action.payload);
        state.owner = action.payload.data;
        state.linkedOwners = toLinkedOwners(action.payload.data);
        state.loading = false;
        state.success = true;
        setStoredOwner(action.payload.data);
      });

    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        // @ts-ignore
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        const nextEmail = String(action.payload?.user?.email || "").trim();
        const nextUserId = String(action.payload?.user?._id || "").trim();
        const nextFirstName = String(action.payload?.user?.firstName || "").trim();
        const nextLastName = String(action.payload?.user?.lastName || "").trim();
        // Update stored user profile (source of truth for identity/billing).
        if (state.currentUser) {
          state.currentUser = {
            userId: state.currentUser.userId,
            email: nextEmail || state.currentUser.email,
            ...(nextFirstName ? { firstName: nextFirstName } : {}),
            ...(nextLastName ? { lastName: nextLastName } : {}),
          };
          setStoredUser(state.currentUser);
        } else if (nextUserId && nextEmail) {
          state.currentUser = {
            userId: nextUserId,
            email: nextEmail,
            ...(nextFirstName ? { firstName: nextFirstName } : {}),
            ...(nextLastName ? { lastName: nextLastName } : {}),
          };
          setStoredUser(state.currentUser);
        }
        state.loading = false;
        state.success = true;
      });

    // ❗Launch campaign❗ (Will be moved to campaign slice later)
    builder
      .addCase(launchCampaign.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(launchCampaign.rejected, (state, action) => {
        // @ts-ignore
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(launchCampaign.fulfilled, (state, action) => {
        // console.log(action.payload);
        state.loading = false;
        state.success = true;
      });

    // ❗Update campaign❗ (Will be moved to campaign slice later)
    builder
      .addCase(updateCampaign.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        // @ts-ignore
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        console.log(action.payload);
        state.loading = false;
        state.success = true;
      });

    builder.addCase(addTagToProspect.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(addTagToProspect.rejected, (state, action) => {
      // @ts-ignore
      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(addTagToProspect.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });

    builder.addCase(removeTagFromProspect.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });
    builder.addCase(createTag.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });
    builder.addCase(updateDailyQuota.pending, (state, action) => {
      state.loading = true;
    });

    builder.addCase(updateDailyQuota.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
    });
  },
});

export const {
  setOwner,
  setUser,
  setCurrentWorkSpace,
  logOut,
  setLists,
  setNewCampaignData,
  setCampaigns,
  setCampaignId,
  setListId,
  setAllProspectsList,
  setTags,
  setActionsAdded,
  removeOwnerCredit,
} = ownerSlice.actions;

export default ownerSlice.reducer;
